import type { SeriesReportOutput } from '@schemas'
export type Report = SeriesReportOutput
export type Year = Report['years'][number]
export type Trend = Report['trends'][number]
export const format = (n: number | null | undefined) =>
  n == null ? 'Unavailable' : n.toLocaleString(undefined, { maximumFractionDigits: 2 })
export const share = (n: number, total: number) => (total > 0 ? (100 * n) / total : null)
export const percent = (n: number | null) => (n === null ? 'Unavailable' : `${format(n)}%`)
// Category chart tokens from global.css, so these marks match the other category charts.
export const palette: Record<string, string> = {
  H: 'var(--class-chart-honey)',
  W: 'var(--class-chart-wax)',
  B: 'var(--class-chart-beverages)',
  A: 'var(--class-chart-art)',
  F: 'var(--class-chart-food)',
  N: 'var(--class-chart-novice)',
}
export function category(cell: Trend['cells'][number]['class']) {
  return cell?.categoryCode ?? cell?.division.code ?? ''
}
export function visibleReport(report: Report, provisional: boolean): Report {
  const years = report.years.filter(y => y.edition && (provisional || !y.edition.provisional))
  const visible = new Set(years.map(y => y.year))
  return {
    ...report,
    years,
    trends: report.trends.map(t => ({ ...t, cells: t.cells.filter(c => visible.has(c.year)) })),
  }
}

export function annualRows(report: Report) {
  const baseline = report.years.find(y => y.edition)?.edition?.metrics
  return report.years.map(({ year, edition }) => ({
    year,
    entries: edition?.metrics.entriesCount ?? null,
    entrants: edition?.metrics.entrantsCount ?? null,
    entriesIndex:
      edition && baseline && baseline.entriesCount > 0
        ? (100 * edition.metrics.entriesCount) / baseline.entriesCount
        : null,
    entrantsIndex:
      edition && baseline && baseline.entrantsCount > 0
        ? (100 * edition.metrics.entrantsCount) / baseline.entrantsCount
        : null,
  }))
}
export function decomposition(previous: Year | undefined, latest: Year | undefined) {
  const a = previous?.edition,
    b = latest?.edition
  if (
    !a ||
    !b ||
    a.provisional ||
    b.provisional ||
    a.metrics.averageEntriesPerEntrant === null ||
    b.metrics.averageEntriesPerEntrant === null
  )
    return null
  const breadth =
    (b.metrics.entrantsCount - a.metrics.entrantsCount) * a.metrics.averageEntriesPerEntrant
  const depth =
    b.metrics.entrantsCount *
    (b.metrics.averageEntriesPerEntrant - a.metrics.averageEntriesPerEntrant)
  return { breadth, depth, total: b.metrics.entriesCount - a.metrics.entriesCount }
}

export function csvCell(value: string | number) {
  const text = String(value)
  // Control characters are intentional: spreadsheet importers may trim them before evaluation.
  // eslint-disable-next-line no-control-regex
  const safe = /^[\s\x00-\x1f]*[=+@-]/.test(text) ? `'${text}` : text
  return `"${safe.replaceAll('"', '""')}"`
}
